import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentAliases } from '../../services/aliasService';
import { ThemeToggle } from '../ui/ThemeToggle';

export function MainLayout() {
  const { isAuthenticated, user, logout } = useAuth();
  const [currentAlias, setCurrentAlias] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      getCurrentAliases()
        .then((aliases) => {
          if (aliases && aliases.length > 0) {
            // Get the first alias (most recent)
            setCurrentAlias(aliases[0].alias);
          }
        })
        .catch((err) => {
          console.error('Failed to load alias:', err);
        });
    } else {
      setCurrentAlias('');
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [isAuthenticated]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-dark-bg text-slate-900 dark:text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3 md:gap-8">
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((prev) => !prev)}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle navigation"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <Link to="/" className="text-xl font-bold gradient-text hover:opacity-80 transition-opacity">
                Privacy Forum
              </Link>
              <nav className="hidden md:flex space-x-1">
                <Link
                  to="/forum"
                  className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition-all"
                >
                  Forum
                </Link>
                {isAuthenticated && (
                  <>
                    <Link
                      to="/messages"
                      className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition-all"
                    >
                      Messages
                    </Link>
                    <Link
                      to="/marketplace"
                      className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition-all"
                    >
                      Marketplace
                    </Link>
                    {(user?.role === 'admin' || user?.role === 'moderator') && (
                      <>
                        <Link
                          to="/admin/categories"
                          className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 px-3 py-2 rounded-lg transition-all font-medium"
                        >
                          Categories
                        </Link>
                        <Link
                          to="/admin/topics"
                          className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 px-3 py-2 rounded-lg transition-all font-medium"
                        >
                          Topics
                        </Link>
                        <Link
                          to="/admin/moderation"
                          className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 px-3 py-2 rounded-lg transition-all font-medium"
                        >
                          Moderation
                        </Link>
                      </>
                    )}
                  </>
                )}
              </nav>
            </div>

            <div className="flex items-center space-x-3">
              <ThemeToggle />
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 font-medium flex flex-col items-end"
                  >
                    <span className="text-sm">{user?.username}</span>
                    {currentAlias && (
                      <span className="text-xs text-brand-600 dark:text-brand-400">
                        @{currentAlias}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 px-4 py-2 rounded-lg text-sm text-slate-900 dark:text-slate-100 font-medium transition-all"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 px-4 py-2 font-medium"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-brand-600 hover:bg-brand-700 px-4 py-2 rounded-lg text-sm text-white font-medium shadow-sm hover:shadow-md transition-all active:scale-[0.98]"
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <>
          <div className="md:hidden fixed inset-0 z-40 bg-slate-900/40" onClick={closeMobileMenu} />
          <div className="md:hidden fixed top-16 inset-x-0 z-50 bg-white dark:bg-dark-card border-b border-slate-200 dark:border-dark-border shadow-lg">
            <nav className="px-4 py-4 space-y-1">
              <Link
                to="/forum"
                onClick={closeMobileMenu}
                className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                Forum
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/messages"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Messages
                  </Link>
                  <Link
                    to="/marketplace"
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Marketplace
                  </Link>
                  {(user?.role === 'admin' || user?.role === 'moderator') && (
                    <div className="pt-2 border-t border-slate-200 dark:border-dark-border space-y-1">
                      <Link
                        to="/admin/categories"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 rounded-lg text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 transition-colors font-medium"
                      >
                        Categories
                      </Link>
                      <Link
                        to="/admin/topics"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 rounded-lg text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 transition-colors font-medium"
                      >
                        Topics
                      </Link>
                      <Link
                        to="/admin/moderation"
                        onClick={closeMobileMenu}
                        className="block px-3 py-2 rounded-lg text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-950/30 transition-colors font-medium"
                      >
                        Moderation
                      </Link>
                    </div>
                  )}
                </>
              )}

              <div className="pt-3 border-t border-slate-200 dark:border-dark-border space-y-2">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center justify-between gap-3 px-1">
                      <div>
                        <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{user?.username}</div>
                        {currentAlias && (
                          <div className="text-xs text-brand-600 dark:text-brand-400">@{currentAlias}</div>
                        )}
                      </div>
                      <Link
                        to="/profile"
                        onClick={closeMobileMenu}
                        className="text-sm text-slate-700 dark:text-slate-200 hover:text-brand-600 dark:hover:text-brand-400 font-medium"
                      >
                        Profile
                      </Link>
                    </div>
                    <button
                      onClick={() => {
                        closeMobileMenu();
                        logout();
                      }}
                      className="w-full px-3 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-sm font-medium text-slate-900 dark:text-slate-100 transition-colors"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <Link
                      to="/login"
                      onClick={closeMobileMenu}
                      className="w-full sm:w-auto px-3 py-2 rounded-lg text-center text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors font-medium"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={closeMobileMenu}
                      className="w-full sm:w-auto px-3 py-2 rounded-lg text-center bg-brand-600 hover:bg-brand-700 text-white font-medium shadow-sm transition-colors"
                    >
                      Register
                    </Link>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </>
      )}

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-card border-t border-slate-200 dark:border-dark-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              &copy; 2025 Privacy Forum by <a href="https://maxint.dev" target="_blank" rel="noopener noreferrer" className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">maxint.dev</a>. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                About
              </Link>
              <Link to="/privacy" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                Privacy
              </Link>
              <Link to="/terms" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                Terms
              </Link>
              <Link to="/contact" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
